import { OptionalFilter } from "../components/SideFilters/interface";
import { AgentAdditionalInfo } from "../modules/ShoppingCart/agentActions/AgentActions";

export interface flightResponseType {
  packages: Flight[];
  total_count: string;
  next_page: number;
}

interface Baggage {
  is_include_suitcase: number;
  is_include_trolly: number;
  suitcase_weight: null;
  trolly_weight: null;
}

interface Location {
  code: string;
  name: string;
}

export enum PricingType {
  PricePerPerson = 'pricePerPerson',
  PriceForAllRooms = 'priceForAllRooms',
  PricePerRoom = 'pricePerRoom',
}

export interface Flight {
  agent_additional_info:AgentAdditionalInfo,
  airline: Airline;
  arrival_date: number;
  baggage: Baggage;
  currency: string;
  departure_date: number;
  duration: string;
  flight_code: string;
  flight_number: string;
  from: Location;
  num_of_stops: number;
  price: {
      adult: number,
      child: number,
      infant: number,
      currency_sign: string
  };
  stop_comment: string;
  to: Location;
  identifier:string
  /**
   * Cabin actually delivered on this segment. Added for multi-cabin support. Always
   * present on segments produced by the Amadeus/Cockpit flight mappers
   * (amadeus-flight.service.ts flightMapper / cockpit-search.service.ts
   * flightMapper) — never omitted, though the value itself can be `null`
   * when the supplier didn't report a cabin for that segment.
   */
  cabin: 'ECONOMY' | 'PREMIUM_ECONOMY' | 'BUSINESS' | 'FIRST' | null;
}

// export interface FlightPrice {
//   adult: number;
//   infant: number;
//   child: number;
//   currency_sign: string;
// }

export enum FlightType {
  Bundle = 'bundle',
  Dynamic = 'one_way',
  OneWay = 'one_way',
  BundleFlight = 'bundle_flight',
  MultiCity = 'multi_city', // matches FlightSubType.multi_city on the BE
}

export interface FlightBundle {
  identifier: string;
  status: string;
  flightFrom: Flight[];
  flightBack: Flight[];
  price: PriceDetails;
  agent_additional_info:AgentAdditionalInfo
  // Best-effort enrichment on flights_bundle[0] only (cheapest-flight APIs)
  destination_airport?: {
    iata_code?: string;
    latitude?: number | null;
    longitude?: number | null;
  };
}

export interface FlightsBundleArray {
  flights_bundle: FlightBundle[];
  optional_filters: OptionalFilter[];
  next_page: number | null;
}
export interface DynamicFlightsArray {
  flights: FlightBundle[];
  optional_filters:OptionalFilter[]
  next_page: number | null;
}

export interface FlightsBundleResponse {
  data: FlightsBundleArray;
  status: string;
}

export interface DynamicFlightResponse {
  data: DynamicFlightsArray;
  status: string;
}

type FlightIdentifier = {
  ts: number;
  t: string;
  id: number;
  fid?: number; // Optional, as it's not present in the top-level identifier
};

type FlightLocation = {
  name: string;
  code: string;
};

type Airline = {
  id: number | string;
  name: string;
  image: string;
  default_supplier_id: null | number;
};

export type FlightBaggage = {
  is_include_suitcase: number;
  suitcase_weight: null | number;
  enable_sale_suitcase: boolean;
  is_include_trolly: number;
  trolly_weight: null | number;
  enable_sale_trolly: boolean;
  suitcase_price?: number;
  trolly_price: number | null;
};

export type FlightView = {
  identifier: FlightIdentifier;
  from: FlightLocation;
  to: FlightLocation;
  airline: Airline;
  flight_number: string;
  num_of_stops: number;
  stop_comment: string;
  departure_date: number;
  arrival_date: number;
  duration: string;
  baggage: FlightBaggage;
  /**
   * Cabin actually delivered on this segment. Added for multi-cabin support. Always
   * present on segments produced by the Amadeus/Cockpit flight mappers
   * (amadeus-flight.service.ts flightMapper / cockpit-search.service.ts
   * flightMapper) — never omitted, though the value itself can be `null`
   * when the supplier didn't report a cabin for that segment. Amadeus can
   * legitimately return a mixed-cabin offer (its multi-city filter uses
   * MOST_SEGMENTS coverage), so this is per-segment by design, not per-offer.
   */
  cabin: 'ECONOMY' | 'PREMIUM_ECONOMY' | 'BUSINESS' | 'FIRST' | null;
};

export type PriceDetails = {
  adult: number;
  child?: number;
  infant?: number;
  currency_sign: string;
  grandTotal: number;
  full_price?: number;
  avg_price?: number;
  // new Amadeus API type:
  additional_services?: string;
  base?: number;
  grand_total?: number;
  total?: number;

  suitcasePrice?: number;
};


type ChangeCancelTerms = {
  adult: string | number;
  child: string | number;
  infant: string | number;
};

type Terms = {
  baggage: string;
  cancel: ChangeCancelTerms;
  change: ChangeCancelTerms;
  operation: string;
  general: string[];
};

export type FightTermsType = {text: {title: string, text: string}, fareBasis: string, segment_id: string, title: string}[];

export type FlightsInfo = {
  identifier: FlightIdentifier;
  description?: string;
  status: string;
  flightFrom: FlightView;
  flight?: FlightView;
  flightBack?: FlightView;
  flights_terms: FightTermsType;
  price: PriceDetails;
  terms: Terms;
  name: string;
  package_type: string;
  agent_additional_info?: AgentAdditionalInfo;
  available?: boolean;
};

export type DynamicPackageFlightsInfo = {
  identifier: FlightIdentifier;
  description?: string;
  status: string;
  flightFrom: FlightView[];
  flightBack?: FlightView[];
  flights_terms: FightTermsType[];
  price: PriceDetails;
  terms: Terms;
  name: string;
  package_type: string;
  agent_additional_info?: AgentAdditionalInfo;
  available?: boolean;
  ts_booking_data?: any;
};

/**
 * A multi-city itinerary. `legs[i]` is one leg's segment
 * array — the same shape `flightFrom` has on a bundle — so each leg can be
 * fed straight to modifyFlightData() and FlightOutLineSection. Mirrors
 * mapMultiCityFlightOffer() in both amadeus-flight.service.ts and
 * cockpit-search.service.ts, which converge on an identical shape.
 */
export interface MultiCityOffer {
  identifier: string;
  status: string;
  legs: FlightView[][];
  /** alias of legs[0], emitted by the BE so existing consumers keep working */
  flightFrom: FlightView[];
  price: PriceDetails;
  numOfStops: { flightFrom: number; legs: number[] };
  agent_additional_info?: AgentAdditionalInfo;
  // ts_booking_data / flights_terms / available / name are NEVER populated at
  // SEARCH time by either provider's multi-city mapper — they attach on the
  // later product-info call (see flightOfferMultiCityInfo, which sets them once
  // the offer has been re-found and priced). So they are undefined on a results
  // page and populated on a view page, exactly as for every other flight
  // product. Keep them optional; a results-page consumer passing them straight
  // to FlightButton2 will always get undefined.
  ts_booking_data?: any;
  flights_terms?: FightTermsType;
  available?: boolean;
  name?: string;
}

export interface MultiCitySearchResponse {
  status: string;
  data: {
    flights_multi_city: MultiCityOffer[];
    optional_filters?: OptionalFilter[];
    /**
     * Present only when non-empty (product-search.service.ts
     * handleMultiCitySearch only spreads `{ errors }` onto `data` when
     * `errors.length`). Each entry carries `provider` plus EITHER `error`
     * (the provider threw) OR `skipped` (it was never called, e.g. a
     * skip_on route rule or Cockpit's 361-day/pax-9 limit) — never both.
     * A connector that cannot do multi-city at all (ALP, SKY360, Ratehawk,
     * Discover, Events365) produces no entry.
     */
    errors?: { provider: 'AMADEUS' | 'COCKPIT'; error?: string; skipped?: string }[];
  };
}

export type PaxCount = {
  adult: number;
  child: number;
  infant: number;
};

export type FlightViewResponse = {
  flightsInfo: FlightsInfo;
  pax: PaxCount;
  is_israel_law_enabled?: boolean;
  eventInfo?: {
    grandTotal: number;
    currency_sign: string;
    suggested_commission?: number;
  };
};

export type DynamicPackageFlightViewResponse = {
  flightsInfo: DynamicPackageFlightsInfo;
  pax: PaxCount;
  is_israel_law_enabled?: boolean;
  eventInfo?: {
    ticketPrice: number;
    serviceFee: number;
    deliveryMethodPrice: number;
    ticketsTotal: number;

    grandTotal: number;
    currency_sign: string;
    suggested_commission?: number;
  };
};
